import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Zap, Sparkles, Calendar, BarChart3, Users, Flame,
  CheckCircle2, ArrowRight, Star, Play, Crown, Gift,
  Facebook, Instagram, Twitter, Youtube
} from 'lucide-react';

const DailyDropLanding = () => {
  const navigate = useNavigate();
  const [isLoggedIn] = useState(!!localStorage.getItem('session_token'));

  const features = [
    {
      icon: Sparkles,
      title: 'AI Content Generator',
      description: 'Generate viral captions, hashtags, and hooks in seconds with our advanced AI.'
    },
    {
      icon: Calendar,
      title: 'Smart Scheduling',
      description: 'Plan and schedule your content across multiple platforms with one click.'
    },
    {
      icon: BarChart3,
      title: 'Analytics Dashboard',
      description: 'Track your growth, engagement, and best-performing content in real-time.'
    },
    {
      icon: Flame,
      title: 'Streak System',
      description: 'Build consistency with daily posting streaks and earn achievement badges.'
    },
    {
      icon: Users,
      title: 'Multi-Platform',
      description: 'Connect Instagram, TikTok, YouTube, X, and Facebook all in one place.'
    },
    {
      icon: Gift,
      title: 'Referral Rewards',
      description: 'Earn credits and free months by inviting your creator friends.'
    }
  ];

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      description: 'Perfect for getting started',
      features: [
        '1 post per day',
        'Basic AI captions',
        '2 platform connections',
        'Basic analytics',
        'Community support'
      ],
      cta: 'Start Free',
      popular: false
    },
    {
      name: 'Pro',
      price: '$19',
      period: '/month',
      description: 'For serious creators',
      features: [
        'Unlimited posts',
        'Advanced AI with hooks & CTAs',
        'All 5 platforms',
        'Advanced analytics',
        'Calendar view',
        'Priority support',
        'No watermarks'
      ],
      cta: 'Go Pro',
      popular: true
    },
    {
      name: 'Pro Max',
      price: '$49',
      period: '/month',
      description: 'For teams & agencies',
      features: [
        'Everything in Pro',
        'Content repurposing AI',
        'Team collaboration',
        'White-label exports',
        'API access',
        'Dedicated support',
        'Custom branding'
      ],
      cta: 'Get Pro Max',
      popular: false
    }
  ];

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Fitness Creator',
      content: 'DailyDrop AI helped me maintain a 60-day streak. My engagement is up 300%!',
      avatar: 'S'
    },
    {
      name: 'Marcus J.',
      role: 'Tech Reviewer',
      content: 'The AI captions are incredible. Saves me hours every week.',
      avatar: 'M'
    },
    {
      name: 'Emma L.',
      role: 'Food Blogger',
      content: 'Finally, one tool for all my platforms. Game changer!',
      avatar: 'E'
    }
  ];

  const handleGetStarted = () => {
    if (isLoggedIn) {
      navigate('/daily-drop');
    } else {
      // Redirect to auth
      const redirectUrl = `${window.location.origin}/auth/callback`;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Navigation */}
      <nav className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="bg-gradient-to-r from-purple-600 to-amber-500 p-2 rounded-lg">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">DailyDrop AI</h1>
              <p className="text-xs text-purple-300">by TerryLBPrompts</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop/pricing')}
            >
              Pricing
            </Button>
            {isLoggedIn ? (
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                onClick={() => navigate('/daily-drop')}
              >
                Dashboard
              </Button>
            ) : (
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                onClick={handleGetStarted}
              >
                Get Started
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="mb-6 bg-purple-500/20 text-purple-300 border-purple-500/30 py-2 px-4">
            <Sparkles className="h-4 w-4 mr-2 inline" />
            AI-Powered Content Creation
          </Badge>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Create Once.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-amber-400">
              Drop Everywhere.
            </span>
          </h1>
          <p className="text-xl text-purple-200/80 max-w-2xl mx-auto mb-8">
            The ultimate creator tool for consistent daily posting. AI-powered captions, 
            multi-platform scheduling, and growth analytics—all in one place.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 h-14 px-8 text-lg"
              onClick={handleGetStarted}
              data-testid="hero-cta"
            >
              Start Free
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20 h-14 px-8"
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <Play className="h-5 w-5 mr-2" />
              See How It Works
            </Button>
          </div>
          {/* Platform Icons */}
          <div className="flex justify-center items-center gap-6 mt-12 opacity-60">
            <Facebook className="h-8 w-8 text-blue-400" />
            <Instagram className="h-8 w-8 text-pink-400" />
            <Twitter className="h-8 w-8 text-gray-400" />
            <Youtube className="h-8 w-8 text-red-400" />
            <span className="text-2xl">📱</span>
          </div>
          <p className="text-purple-300/50 text-sm mt-4">Works with all major platforms</p>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-20 px-4 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Everything You Need to Grow</h2>
            <p className="text-purple-200/70 max-w-xl mx-auto">
              Powerful features designed to help you create consistently and grow your audience.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="bg-black/30 border-purple-500/20 backdrop-blur-sm hover:border-purple-500/40 transition-all group"
              >
                <CardContent className="p-6">
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <feature.icon className="h-6 w-6 text-purple-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-purple-300/60 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Simple, Transparent Pricing</h2>
            <p className="text-purple-200/70 max-w-xl mx-auto">
              Start free and upgrade as you grow. No hidden fees.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, index) => (
              <Card 
                key={index}
                className={`backdrop-blur-sm relative ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-purple-600/30 to-pink-600/30 border-purple-500/50' 
                    : 'bg-black/30 border-purple-500/20'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-none">
                      <Crown className="h-3 w-3 mr-1" /> Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold text-xl mb-1">{plan.name}</h3>
                  <p className="text-purple-300/60 text-sm mb-4">{plan.description}</p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-purple-300/60">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                        <span className="text-purple-200">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500' 
                        : 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-200'
                    }`}
                    onClick={handleGetStarted}
                    data-testid={`plan-${plan.name.toLowerCase()}-cta`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-black/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Loved by Creators</h2>
            <p className="text-purple-200/70">See what our community is saying</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-purple-200 mb-4">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="text-white font-medium">{testimonial.name}</p>
                      <p className="text-purple-300/60 text-sm">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 border-purple-500/30 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to Grow Your Audience?
              </h2>
              <p className="text-purple-200/80 mb-8 max-w-xl mx-auto">
                Join thousands of creators using DailyDrop AI to build their online presence consistently.
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 h-14 px-12 text-lg"
                onClick={handleGetStarted}
                data-testid="final-cta"
              >
                Start Your Free Trial
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <p className="text-purple-300/50 text-sm mt-4">No credit card required</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-purple-500/20 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-400" />
            <span className="text-purple-200">DailyDrop AI</span>
            <span className="text-purple-300/50">by TerryLBPrompts.com</span>
          </div>
          <div className="flex items-center gap-6 text-purple-300/60 text-sm">
            <a href="#" className="hover:text-purple-200">Terms</a>
            <a href="#" className="hover:text-purple-200">Privacy</a>
            <a href="#" className="hover:text-purple-200">Support</a>
          </div>
          <p className="text-purple-300/40 text-sm">© 2026 TerryLBPrompts. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DailyDropLanding;
